import { useAuth, useOrganization } from '@clerk/clerk-react';
import { Button, Label, Progress } from '@startupquickstart/frontend';
import { ImageIcon, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

interface ImageUploadDropzoneProps {
  value?: string;
  onChange: (mediaId: string | undefined) => void;
  label?: string;
  maxSizeMB?: number;
  required?: boolean;
  error?: string;
}

/**
 * Beautiful drag-and-drop image upload component
 * Uploads to /media endpoint and returns media ID
 */
export function ImageUploadDropzone({
  value,
  onChange,
  label = 'Featured Image',
  maxSizeMB = 5,
  required = false,
  error,
}: ImageUploadDropzoneProps) {
  const { getToken } = useAuth();
  const { organization } = useOrganization();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    value ? `/api/v1/media/${value}` : undefined,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  /**
   * Validate file type and size
   */
  const validateFile = useCallback(
    (file: File): string | null => {
      if (!file.type.startsWith('image/')) {
        return 'Please upload an image file';
      }
      if (file.size > maxSizeBytes) {
        return `File size must be less than ${maxSizeMB}MB`;
      }
      return null;
    },
    [maxSizeBytes, maxSizeMB],
  );

  /**
   * Upload file to server
   */
  const uploadFile = useCallback(
    async (file: File) => {
      try {
        setIsUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', file);

        // Simulate progress (replace with actual progress if API supports it)
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 10, 90));
        }, 200);

        // Use fetch directly instead of apiClient to avoid JSON transformation
        const token = await getToken();
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
        };

        const fetchResponse = await fetch('/api/v1/media', {
          method: 'POST',
          body: formData,
          headers,
        });

        if (!fetchResponse.ok) {
          const errorData = await fetchResponse.json();
          throw new Error(errorData.error || 'Upload failed');
        }

        const response = await fetchResponse.json();

        clearInterval(progressInterval);
        setUploadProgress(100);

        // Set the media ID
        const data = response.data || response;
        onChange(data.id);
        setPreviewUrl(data.url);

        toast.success('Image uploaded successfully');
      } catch (err) {
        const errorMessage = (err as Error)?.message || 'Failed to upload image';
        toast.error(errorMessage);
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [getToken, onChange, organization?.id],
  );

  /**
   * Handle file selection
   */
  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      uploadFile(file);
    },
    [validateFile, uploadFile],
  );

  /**
   * Handle drag events
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle file input change
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle remove image
   */
  const handleRemove = () => {
    onChange(undefined);
    setPreviewUrl(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Open file picker
   */
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="grid gap-2">
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />

      {previewUrl && !isUploading ? (
        <div className="relative group">
          <div className="relative overflow-hidden rounded-lg border-2 border-border bg-muted h-48 flex items-center justify-center p-4">
            <img
              src={previewUrl}
              alt="Preview"
              className="h-full w-auto object-contain rounded-lg"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleClick}>
                <Upload className="h-4 w-4 mr-2" />
                Replace
              </Button>
              <Button type="button" variant="destructive" size="sm" onClick={handleRemove}>
                <X className="h-4 w-4 mr-2" />
                Remove
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative cursor-pointer rounded-lg border-2 border-dashed transition-all
            ${isDragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/50'}
            ${isUploading ? 'pointer-events-none' : 'hover:border-primary hover:bg-primary/5'}
            h-48 flex flex-col items-center justify-center gap-3 p-6
          `}
        >
          {isUploading ? (
            <>
              <Upload className="h-12 w-12 text-muted-foreground animate-pulse" />
              <div className="w-full max-w-xs space-y-2">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-sm text-muted-foreground text-center">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-full bg-primary/10 p-4">
                <ImageIcon className="h-8 w-8 text-primary" />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium">
                  <span className="text-primary">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">PNG, JPG, GIF up to {maxSizeMB}MB</p>
              </div>
            </>
          )}
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
